import { Component } from 'react';
import type { ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional label shown in the error card (e.g. "Library") */
  screenName?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * React class-based error boundary.
 *
 * Wraps individual route screens so that an unhandled render error shows
 * a recoverable error card instead of blanking the entire application.
 * Without this, a single screen crash unmounts the whole React tree and
 * leaves a blank window that only recovers after a full app restart.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: unknown): State {
    const message =
      error instanceof Error ? error.message : String(error);
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, info: { componentStack: string }) {
    // Log to console so DevTools still shows the trace
    console.error('[ErrorBoundary] Screen crashed:', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: '' });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const screen = this.props.screenName ?? 'This screen';

    return (
      <div style={{
        padding: '40px 32px',
        maxWidth: 560,
        margin: '0 auto',
        fontFamily: 'system-ui, sans-serif',
      }}>
        <div style={{
          background: '#fff',
          border: '1px solid #e0ddd8',
          borderLeft: '3px solid #c44a1a',
          borderRadius: 0,
          padding: '24px 28px',
        }}>
          <div style={{
            fontFamily: 'Courier New, monospace',
            fontSize: 10,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: '#7a7d8a',
            marginBottom: 12,
          }}>
            SCREEN ERROR
          </div>
          <h3 style={{
            fontFamily: 'Georgia, serif',
            fontSize: 17,
            fontWeight: 'normal',
            color: '#1a1f2e',
            margin: '0 0 10px',
          }}>
            {screen} failed to render
          </h3>
          <p style={{
            fontSize: 13,
            color: '#7a7d8a',
            margin: '0 0 6px',
            lineHeight: 1.6,
          }}>
            An unexpected error occurred. The rest of the application is unaffected.
          </p>
          {this.state.message && (
            <pre style={{
              fontFamily: 'Courier New, monospace',
              fontSize: 11,
              background: '#f0ede8',
              border: '1px solid #d0ccc4',
              padding: '10px 12px',
              margin: '14px 0 18px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: '#c44a1a',
            }}>
              {this.state.message}
            </pre>
          )}
          <button
            onClick={this.handleReset}
            style={{
              fontFamily: 'system-ui, sans-serif',
              fontSize: 13,
              padding: '7px 18px',
              background: '#1a1f2e',
              color: '#fff',
              border: 'none',
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
